import { BrowserModule } from '@angular/platform-browser';
import { NgModule } from '@angular/core';
import { LOCALE_ID } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { RouterModule, Routes } from '@angular/router';
import { AgmCoreModule } from '@agm/core';
import { HttpClientModule, HttpClient } from '@angular/common/http';

import { TranslateModule, TranslateLoader } from '@ngx-translate/core';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';

import { GoogleAnalyticsModule, GA_TOKEN } from 'angular-ga';

import { AngularFirestoreModule } from '@angular/fire/firestore';
import { AngularFireModule } from '@angular/fire';
import { AngularFireStorageModule, StorageBucket } from '@angular/fire/storage';
import { ArchwizardModule } from 'ng2-archwizard';

/* Bootstrap */
import { BsDropdownModule } from 'ngx-bootstrap/dropdown';
import { TooltipModule } from 'ngx-bootstrap/tooltip';
import { ModalModule } from 'ngx-bootstrap/modal';

import { AppComponent } from './app.component';

/* Page Components */
import { MasterModule } from './master/master.module';
import { MasterComponent } from './master/master.component';

/* Services */
import { JobDataService } from './services/job-data.service';
import { MetaDataService } from './services/meta-data.service';
import { PostcodeManagerService } from './services/postcode-manager.service';
import { PricingService } from './services/pricing.service';
import { PopupService } from './services/popup.service';
import { APIService } from './services/api.service';
import { LanguageService } from './services/language.service';
import { RouterService } from './services/router.service';

/* Views */
import { HomeComponent } from './home/home.component';
import { PrivacyComponent } from './privacy/privacy.component';
import { TermsComponent } from './terms/terms.component';
import { FaqComponent } from './faq/faq.component';
import { ContactusComponent } from './contactus/contactus.component';
import { TriageComponent } from './triage/triage.component';
import { CompleteComponent } from './complete/complete.component';
import { StartComponent } from './start/start.component';
import { TapsComponent } from './taps/taps.component';
import { CompleteinspectionComponent } from './completeinspection/completeinspection.component';
import { EmergencyComponent } from './emergency/emergency.component';
import { EmergencyCompleteComponent } from './emergency-complete/emergency-complete.component';
import { CardpaymentComponent } from './cardpayment/cardpayment.component';
import { CardpaymentCompleteComponent } from './cardpayment-complete/cardpayment-complete.component';

/* Material components */
import { HSMaterialModule } from '../material-module';
import { MatNativeDateModule } from '@angular/material/core';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { ToastrModule } from 'ngx-toastr';

import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';

import { environment } from '../environments/environment';
import { Clients, QuestionTypes, TradeTypes, JobTypes } from './services/globals.service';

// Triage question components
import { TriageQuestionFreeTextComponent } from './triage-components/triage-questions/triage-question-free-text/triage-question-free-text.component';
import { TriageQuestionBookingWindowsComponent } from './triage-components/triage-questions/triage-question-booking-windows/triage-question-booking-windows.component';
import { TriageQuestionContactDetailsComponent } from './triage-components/triage-questions/triage-question-contact-details/triage-question-contact-details.component';
import { TriageQuestionConfirmationComponent } from './triage-components/triage-questions/triage-question-confirmation/triage-question-confirmation.component';
import { TriageQuestionQuantityComponent } from './triage-components/triage-questions/triage-question-quantity/triage-question-quantity.component';
import { TriageQuestion2OptionsComponent } from './triage-components/triage-questions/triage-question2-options/triage-question2-options.component';
import { TriageQuestionTapModelComponent } from './triage-components/triage-questions/triage-question-tap-model/triage-question-tap-model.component';
import { TriageQuestionNumericComponent } from './triage-components/triage-questions/triage-question-numeric/triage-question-numeric.component';
import { TriageQuestionYesNoComponent } from './triage-components/triage-questions/triage-question-yes-no/triage-question-yes-no.component';
import { TriageQuestionSimple2OptionsComponent } from './triage-components/triage-questions/triage-question-simple2-options/triage-question-simple2-options.component';
import { TriageQuestionHandymanDurationComponent } from './triage-components/triage-questions/triage-question-handyman-duration/triage-question-handyman-duration.component';
import { TriageQuestionJobNotesPhotosComponent } from './triage-components/triage-questions/triage-question-job-notes-photos/triage-question-job-notes-photos.component';
import { TriageQuestionCarpentryDurationComponent } from './triage-components/triage-questions/triage-question-carpentry-duration/triage-question-carpentry-duration.component';
import { TriageQuestionPlasteringDurationComponent } from './triage-components/triage-questions/triage-question-plastering-duration/triage-question-plastering-duration.component';
import { TriageQuestionPaintingDurationComponent } from './triage-components/triage-questions/triage-question-painting-duration/triage-question-painting-duration.component';
import { TriageQuestionHVACDurationComponent } from './triage-components/triage-questions/triage-question-hvacduration/triage-question-hvacduration.component';
import { TriageQuestionRoofPlumbingDurationComponent } from './triage-components/triage-questions/triage-question-roof-plumbing-duration/triage-question-roof-plumbing-duration.component';
import { TriageQuestionFencingDurationComponent } from './triage-components/triage-questions/triage-question-fencing-duration/triage-question-fencing-duration.component';
import { TriageQuestionBrickLayingDurationComponent } from './triage-components/triage-questions/triage-question-brick-laying-duration/triage-question-brick-laying-duration.component';
import { TriageQuestionBrickPavingDurationComponent } from './triage-components/triage-questions/triage-question-brick-paving-duration/triage-question-brick-paving-duration.component';
import { TriageQuestionAntennasDurationComponent } from './triage-components/triage-questions/triage-question-antennas-duration/triage-question-antennas-duration.component';
import { TriageQuestionTilingDurationComponent } from './triage-components/triage-questions/triage-question-tiling-duration/triage-question-tiling-duration.component';
import { TriageQuestionPaintingWholeApartmentComponent } from './triage-components/triage-questions/triage-question-painting-whole-apartment/triage-question-painting-whole-apartment.component';
import { TriageQuestionPaintingSingleRoomComponent } from './triage-components/triage-questions/triage-question-painting-single-room/triage-question-painting-single-room.component';
import { TriageQuestionGardeningDurationComponent } from './triage-components/triage-questions/triage-question-gardening-duration/triage-question-gardening-duration.component';
import { TriageQuestionGutterCleaningDurationComponent } from './triage-components/triage-questions/triage-question-gutter-cleaning-duration/triage-question-gutter-cleaning-duration.component';
import { TriageQuestionSolarCleaningDurationComponent } from './triage-components/triage-questions/triage-question-solar-cleaning-duration/triage-question-solar-cleaning-duration.component';
import { TriageQuestionAirconDurationComponent } from './triage-components/triage-questions/triage-question-aircon-duration/triage-question-aircon-duration.component';
import { TriageQuestionSmokeAlarmsDurationComponent } from './triage-components/triage-questions/triage-question-smoke-alarms-duration/triage-question-smoke-alarms-duration.component';

import { TriageSidebarComponent } from './triage-components/triage-sidebar/triage-sidebar.component';
import { ClickoutLinkComponent } from './clickout-link/clickout-link.component';

const appRoutes: Routes = [
  {
    path: 'home',
    component: MasterComponent,
    children: [
      {
        outlet: 'master',
        path: '',
        component: HomeComponent
      }
    ]
  },

  {
    path: 'faq',
    component: MasterComponent,
    children: [
      {
        outlet: 'master',
        path: '',
        component: FaqComponent
      }
    ]
  },

  {
    path: 'contactus',
    component: MasterComponent,
    children: [
      {
        outlet: 'master',
        path: '',
        component: ContactusComponent
      }
    ]
  },

  {
    path: 'privacy',
    component: MasterComponent,
    children: [
      {
        outlet: 'master',
        path: '',
        component: PrivacyComponent
      }
    ]
  },

  {
    path: 'terms',
    component: MasterComponent,
    children: [
      {
        outlet: 'master',
        path: '',
        component: TermsComponent
      }
    ]
  },

  {
    path: 'emergency',
    component: MasterComponent,
    children: [
      {
        outlet: 'master',
        path: '',
        component: EmergencyComponent
      }
    ]
  },

  {
    path: 'triage',
    component: MasterComponent,
    children: [
      {
        outlet: 'master',
        path: '',
        component: TriageComponent
      }
    ]
  },

  {
    path: 'complete',
    component: MasterComponent,
    children: [
      {
        outlet: 'master',
        path: '',
        component: CompleteComponent
      }
    ]
  },

  {
    path: 'emergencycomplete',
    component: MasterComponent,
    children: [
      {
        outlet: 'master',
        path: '',
        component: EmergencyCompleteComponent
      }
    ]
  },

  {
    path: 'completeinspection',
    component: MasterComponent,
    children: [
      {
        outlet: 'master',
        path: '',
        component: CompleteinspectionComponent
      }
    ]
  },

  {
    path: 'start',
    component: MasterComponent,
    children: [
      {
        outlet: 'master',
        path: '',
        component: StartComponent
      }
    ]
  },

  {
    path: 'taps',
    component: MasterComponent,
    children: [
      {
        outlet: 'master',
        path: '',
        component: TapsComponent
      }
    ]
  },

  {
    path: 'cardpayment',
    component: MasterComponent,
    children: [
      {
        outlet: 'master',
        path: '',
        component: CardpaymentComponent
      }
    ]
  },

  {
    path: 'cardpaymentcomplete',
    component: MasterComponent,
    children: [
      {
        outlet: 'master',
        path: '',
        component: CardpaymentCompleteComponent
      }
    ]
  },

  {
    path: '',
    redirectTo: '/home',
    pathMatch: 'full'
  }
];

@NgModule({
  declarations: [
    AppComponent,
    MasterComponent,
    HomeComponent,
    TriageComponent,
    CompleteComponent,
    StartComponent,
    TapsComponent,
    FaqComponent,
    ContactusComponent,
    PrivacyComponent,
    TermsComponent,
    CompleteinspectionComponent,
    EmergencyComponent,
    EmergencyCompleteComponent,
    CardpaymentComponent,
    CardpaymentCompleteComponent,
    TriageQuestionFreeTextComponent,
    TriageQuestionBookingWindowsComponent,
    TriageQuestionContactDetailsComponent,
    TriageQuestionConfirmationComponent,
    TriageQuestionQuantityComponent,
    TriageQuestion2OptionsComponent,
    TriageQuestionTapModelComponent,
    TriageQuestionNumericComponent,
    TriageQuestionYesNoComponent,
    TriageQuestionSimple2OptionsComponent,
    TriageQuestionHandymanDurationComponent,
    TriageQuestionJobNotesPhotosComponent,
    TriageQuestionCarpentryDurationComponent,
    TriageQuestionPlasteringDurationComponent,
    TriageQuestionPaintingDurationComponent,
    TriageQuestionHVACDurationComponent,
    TriageQuestionRoofPlumbingDurationComponent,
    TriageQuestionFencingDurationComponent,
    TriageQuestionBrickLayingDurationComponent,
    TriageQuestionBrickPavingDurationComponent,
    TriageQuestionAntennasDurationComponent,
    TriageQuestionTilingDurationComponent,
    TriageQuestionPaintingWholeApartmentComponent,
    TriageQuestionPaintingSingleRoomComponent,
    TriageQuestionGardeningDurationComponent,
    TriageQuestionGutterCleaningDurationComponent,
    TriageQuestionSolarCleaningDurationComponent,
    TriageQuestionAirconDurationComponent,
    TriageQuestionSmokeAlarmsDurationComponent,
    TriageSidebarComponent,
    ClickoutLinkComponent,
  ],
  imports: [
    BrowserModule,
    MasterModule,
    FormsModule,
    ReactiveFormsModule,
    HttpClientModule,
    ArchwizardModule,
    ToastrModule.forRoot(),
    GoogleAnalyticsModule.forRoot(),
    FontAwesomeModule,
    TranslateModule,
    HSMaterialModule,
    MatNativeDateModule,
    BrowserAnimationsModule,

    AngularFireModule.initializeApp(environment.firebase),
    AngularFirestoreModule,
    AngularFireStorageModule,
    
    RouterModule.forRoot(
      appRoutes, {
        onSameUrlNavigation: 'reload'
      }
    ),
    
    AgmCoreModule.forRoot({
      apiKey: 'AIzaSyD1VK91D1yQKjZHgfNb2-W_AfYiBpdOym4',
      libraries: ['places'],
    }),

    TranslateModule.forRoot({
      defaultLanguage: 'default',
      loader: {
          provide: TranslateLoader,
          useFactory: HttpLoaderFactory,
          deps: [HttpClient]
      }
    }),
  ],
  providers: [
    JobDataService,
    MetaDataService,
    PostcodeManagerService,
    PricingService,
    Clients, QuestionTypes, TradeTypes, JobTypes,
    PopupService,
    APIService,
    LanguageService,
    RouterService,
    { provide: StorageBucket, useValue: environment.firebase.storageBucket },
    { provide: LOCALE_ID, useValue: 'en-US' },
    { provide: GA_TOKEN, useValue: 'UA-128468582-9' },
  ],
  bootstrap: [AppComponent]
})
export class AppModule { }

export function HttpLoaderFactory(http: HttpClient) {
  return new TranslateHttpLoader(http);
}
